import { lazy, Suspense, useMemo, useState } from "react";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  usePopupTimings,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import type {
  CustomHomePopupItem,
  CustomHomePopupResult,
} from "./useCustomHomePopup.types";
import { useUserDataFlattened } from "@/modules";

const CustomHomePopup = lazy(() =>
  import("../../CustomHomePopup/CustomHomePopup").then(m => ({
    default: m.CustomHomePopup,
  }))
);

const CUSTOM_HOME_POPUP_CONFIG = POPUP_CONFIGS.CUSTOM_HOME_POPUP;

export const useCustomHomePopup = (): CustomHomePopupResult => {
  const popupTimings = usePopupTimings();
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 获取进入首页的自定义弹窗数据
  const customItems = useMemo(() => {
    if (!popupTimings) return [];

    const enterHomePopup = popupTimings.find(
      timing => timing.timingType === "ENTER_HOME_PAGE"
    );

    if (!enterHomePopup || !enterHomePopup.enabled) return [];

    const items = (enterHomePopup.customItems || [])
      .filter(item => item.enabled)
      .sort((a, b) => a.sort - b.sort);
    return items as CustomHomePopupItem[];
  }, [popupTimings]);

  const hasItems = customItems.length > 0;

  // 获取优先级：优先使用 customItems 中第一个的 sort 值，否则使用默认值
  const priority = CUSTOM_HOME_POPUP_CONFIG.priority;

  const CustomHomePopupContent: React.FC<{ onComplete: () => void }> = ({
    onComplete,
  }) => {
    const [visible, setVisible] = useState(true);

    const handleClose = () => {
      setVisible(false);
      onComplete();
    };

    return (
      <Suspense fallback={null}>
        <CustomHomePopup
          visible={visible}
          items={customItems}
          onClose={handleClose}
        />
      </Suspense>
    );
  };

  const { trigger } = useQueuedPopup({
    popupId: CUSTOM_HOME_POPUP_CONFIG.id,
    priority,
    shouldShow: () => isConfigReady && hasItems,
    renderContent: onComplete => (
      <CustomHomePopupContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: false,
    autoTrigger: true,
    dependencies: [isConfigReady, customItems.length, isAuthenticated],
    userBinding: true,
    getUserId: () => userInfo?.userId,
  });

  return {
    items: customItems,
    hasItems,
    trigger,
  };
};
